/**
 * Central chart-config module — the single place a board gets its colours,
 * severity semantics, heat ramps and value formatters from.
 *
 * Two colour families, per the dashboard design rules (§3):
 *
 * - **Categorical = identity** (subscription, SKU, workload type): fixed
 *   assignment from the `--norican-chart-1..16` ramp, in an order derived from
 *   the FULL entity list — never from a filtered slice, so a filter cannot
 *   repaint the survivors. Build the config once from the unfiltered payload.
 * - **Status is reserved** (severity, state): success/warning/danger tones and
 *   the danger/neutral sequential scales. Severity is a status scale, never a
 *   categorical one, and is never colour-alone — every entry carries an icon
 *   and a label to pair with.
 *
 * Everything here resolves to DS tokens (`var(--norican-*)`); no hex anywhere.
 * Runtime colour helpers come from `@norican/react`; `@norican/dataviz` is
 * imported type-only, so this module adds no chart runtime to non-chart panels.
 */

import type { ChartConfig } from "@norican/dataviz";
import {
  CircleAlert,
  CircleEllipsis,
  type IconComponent,
  Info,
  OctagonAlert,
  TriangleAlert,
} from "@norican/icons";
import {
  chartColors,
  sequentialFill,
  type SequentialRamp,
  sequentialRamp,
  type SequentialRampOptions,
  type SequentialScale,
  sequentialScale,
} from "@norican/react";

/* ------------------------------------------------------------------------ *
 * Tones (shared vocabulary with `features/cloud/status.ts`)
 * ------------------------------------------------------------------------ */

/** Tone accepted by `Badge`, `Statistic`, `Card` accents and `MiniSparkline`. */
export type StatusTone = "success" | "warning" | "danger" | "neutral";

/** Tone accepted by `StatisticBar` items, `StatusStrip.Cell` and `Freshness`. */
export type StripTone = "positive" | "caution" | "negative" | "neutral";

/* ------------------------------------------------------------------------ *
 * Categorical identity configs (fixed entity order)
 * ------------------------------------------------------------------------ */

/**
 * How many leading entries of `chartColors` are Norican's business-unit colours.
 *
 * `--norican-chart-1…5` are Simpson (#077728, green), Monitizer (#f7c037,
 * amber), Striko Westofen (#ba0c32, red), DISA (#53a3bf) and Wheelabrator
 * (#1a3362). They are the corporate identity and exactly right when the
 * categories ARE the business units — and wrong for anything else, because the
 * first three are the universal good/degraded/broken triad. A chart that ranks
 * harm and paints its worst bar Simpson green tells the reader the opposite of
 * the truth, and `/dynamics/pinpoint` did precisely that.
 */
const BUSINESS_COLOURS = 5;

/** The neutral half of the ramp: `--norican-chart-6…16`, no status reading. */
const NEUTRAL_COLOURS = chartColors.slice(BUSINESS_COLOURS);

/** Which half of the ramp a categorical config draws from. */
export type IdentityPalette =
  /** Neutral hues — the default, and correct for anything that is not a business unit. */
  | "categorical"
  /** Simpson, Monitizer, Striko, DISA, Wheelabrator, in that order. */
  | "business";

/**
 * A `ChartConfig` whose colours are assigned from the categorical ramp in the
 * order given — the caller fixes the order once (from the full entity list)
 * and every chart on the board shares the same assignment.
 *
 * Draws from the NEUTRAL hues by default. Status colours are reserved
 * (`.claude/rules/dashboard-design.md` §3.2), and a categorical scale that
 * begins green, amber, red hands a severity reading to whichever category
 * happens to be first — which is a property of the alphabet, not of the data.
 * Pass `"business"` only when the keys really are the business units.
 */
export function identityConfig(
  keys: readonly string[],
  labels?: Readonly<Record<string, string>>,
  palette: IdentityPalette = "categorical",
): ChartConfig {
  const ramp = palette === "business" ? chartColors : NEUTRAL_COLOURS;
  const config: ChartConfig = {};
  keys.forEach((key, index) => {
    config[key] = {
      label: labels?.[key] ?? key,
      color: ramp[index % ramp.length],
    };
  });
  return config;
}

/**
 * Fixed categorical assignment for Azure subscriptions, ordered by display
 * name. Pass every known subscription (e.g. `overview.subscriptions`), not the
 * filtered set, so drilling into one subscription keeps everyone's colour.
 */
export function subscriptionConfig(
  subscriptions: readonly { name: string; subscription_id?: string }[],
): ChartConfig {
  const names = [...new Set(subscriptions.map((s) => s.name))].sort((a, b) => a.localeCompare(b));
  return identityConfig(names);
}

/**
 * Fixed categorical assignment for licence SKUs, keyed and ordered by
 * `sku_part_number` (stable across payloads), labelled with the display name.
 */
export function skuConfig(
  skus: readonly { sku_part_number: string; name?: string | null }[],
): ChartConfig {
  const byPart = new Map(skus.map((s) => [s.sku_part_number, s.name ?? s.sku_part_number]));
  const parts = [...byPart.keys()].sort((a, b) => a.localeCompare(b));
  return identityConfig(parts, Object.fromEntries(parts.map((p) => [p, byPart.get(p) ?? p])));
}

/**
 * Fixed categorical assignment for workload / resource types, ordered
 * alphabetically so the assignment is independent of payload order.
 */
export function workloadTypeConfig(
  types: readonly string[],
  labels?: Readonly<Record<string, string>>,
): ChartConfig {
  const unique = [...new Set(types)].sort((a, b) => a.localeCompare(b));
  return identityConfig(unique, labels);
}

/* ------------------------------------------------------------------------ *
 * Advisor categories (categorical identity — NOT status)
 * ------------------------------------------------------------------------ */

/**
 * Azure Advisor's five categories in the fixed order the board lists them.
 * These are identities, not statuses — a Cost recommendation is not "good"
 * and a HighAvailability one not "broken" — so they wear the categorical
 * ramp, not success/danger tones (the old `CATEGORY_COLOR` anti-pattern).
 */
export const ADVISOR_CATEGORY_ORDER = [
  "HighAvailability",
  "Security",
  "Performance",
  "Cost",
  "OperationalExcellence",
] as const;

export type AdvisorCategory = (typeof ADVISOR_CATEGORY_ORDER)[number];

export const ADVISOR_CATEGORY_LABEL: Record<AdvisorCategory, string> = {
  HighAvailability: "High availability",
  Security: "Security",
  Performance: "Performance",
  Cost: "Cost",
  OperationalExcellence: "Operational excellence",
};

/** Fixed-order categorical `ChartConfig` for Advisor categories. */
export const ADVISOR_CATEGORY_CONFIG: ChartConfig = identityConfig(
  ADVISOR_CATEGORY_ORDER,
  ADVISOR_CATEGORY_LABEL,
);

/** Colour for one Advisor category; muted token for anything unknown. */
export function advisorCategoryColor(category: string): string {
  return ADVISOR_CATEGORY_CONFIG[category]?.color ?? "var(--norican-bg-muted)";
}

/** Label for one Advisor category; passes unknown values through. */
export function advisorCategoryLabel(category: string): string {
  return ADVISOR_CATEGORY_LABEL[category as AdvisorCategory] ?? category;
}

/* ------------------------------------------------------------------------ *
 * Severity — a STATUS scale (never categorical colours)
 * ------------------------------------------------------------------------ */

/** Azure Monitor's severity enum, most severe first. */
export const SEVERITY_ORDER = ["Sev0", "Sev1", "Sev2", "Sev3", "Sev4"] as const;

export type AlertSeverity = (typeof SEVERITY_ORDER)[number];

export interface SeverityStatus {
  /** Full label, e.g. "Sev 0 · Critical". */
  label: string;
  /** Compact label for axes and legends, e.g. "Sev 0". */
  short: string;
  /** `Badge` / `Statistic` tone. */
  tone: StatusTone;
  /** `StatisticBar` / `StatusStrip` tone. */
  stripTone: StripTone;
  /** Chart fill: danger-scale intensity for Sev0–2, neutral scale below. */
  color: string;
  /** Icon to pair with the colour — severity is never colour-alone. */
  icon: IconComponent;
}

const dangerScale = sequentialScale("danger");
const neutralScale = sequentialScale("neutral");

/**
 * Sev0–4 mapped onto status semantics: outage-grade severities descend the
 * danger intensity scale, Sev2 wears the warning token, Sev3/Sev4 are
 * informational noise on the neutral scale. Never the categorical ramp.
 */
export const SEVERITY_STATUS: Record<AlertSeverity, SeverityStatus> = {
  Sev0: {
    label: "Sev 0 · Critical",
    short: "Sev 0",
    tone: "danger",
    stripTone: "negative",
    color: dangerScale[4] ?? "var(--norican-danger)",
    icon: OctagonAlert,
  },
  Sev1: {
    label: "Sev 1 · Error",
    short: "Sev 1",
    tone: "danger",
    stripTone: "negative",
    color: dangerScale[2] ?? "var(--norican-danger)",
    icon: CircleAlert,
  },
  Sev2: {
    label: "Sev 2 · Warning",
    short: "Sev 2",
    tone: "warning",
    stripTone: "caution",
    color: "var(--norican-warning)",
    icon: TriangleAlert,
  },
  Sev3: {
    label: "Sev 3 · Informational",
    short: "Sev 3",
    tone: "neutral",
    stripTone: "neutral",
    color: neutralScale[2] ?? "var(--norican-bg-muted)",
    icon: Info,
  },
  Sev4: {
    label: "Sev 4 · Verbose",
    short: "Sev 4",
    tone: "neutral",
    stripTone: "neutral",
    color: neutralScale[1] ?? "var(--norican-bg-muted)",
    icon: CircleEllipsis,
  },
};

const FALLBACK_SEVERITY: SeverityStatus = {
  label: "Unknown severity",
  short: "Unknown",
  tone: "neutral",
  stripTone: "neutral",
  color: neutralScale[0] ?? "var(--norican-bg-muted)",
  icon: CircleEllipsis,
};

/** Severity semantics for a raw backend string, safe for unknown values. */
export function severityStatus(severity: string | null | undefined): SeverityStatus {
  return SEVERITY_STATUS[severity as AlertSeverity] ?? FALLBACK_SEVERITY;
}

/**
 * `ChartConfig` for severity charts (donut of the severity mix, stacked
 * volume): keys `Sev0..Sev4`, status colours, short labels. Spread extra
 * series in as needed.
 */
export const SEVERITY_CONFIG: ChartConfig = Object.fromEntries(
  SEVERITY_ORDER.map((sev) => [
    sev,
    { label: SEVERITY_STATUS[sev].short, color: SEVERITY_STATUS[sev].color },
  ]),
);

/* ------------------------------------------------------------------------ *
 * Sequential ramps (heat-maps, treemap intensity)
 * ------------------------------------------------------------------------ */

export type { SequentialRamp, SequentialRampOptions, SequentialScale };

/**
 * Fill for one heat-map / density cell from a normalised intensity `t` (0..1).
 * One hue light→dark, theme-aware. Families: `neutral | accent | success |
 * danger | info` (default `info`).
 */
export function heatFill(t: number | null | undefined, scale: SequentialScale = "info"): string {
  return sequentialFill(t, scale);
}

/** The 5 stepped fills of a ramp, low→high — for rendering a ramp legend. */
export function heatLegend(scale: SequentialScale = "info"): string[] {
  return sequentialScale(scale);
}

/**
 * Derive a custom ramp from seed colours when no built-in family fits
 * (`Chart.sequentialRamp` re-exported). Spread `ramp.containerProps` on an
 * ancestor of the cells, then paint with `ramp.fill(t)`.
 */
export const customRamp: (options: SequentialRampOptions) => SequentialRamp = sequentialRamp;

/* ------------------------------------------------------------------------ *
 * Shared value formatters
 * ------------------------------------------------------------------------ */

const EM_DASH = "—";

/** Thousands-separated integer, or an em dash when there is nothing to show. */
export function formatCount(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return value.toLocaleString();
}

/** Compact count for axis ticks and dense tiles — `12.4K`, `1.2M`. */
export function formatCountCompact(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return value.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

/**
 * A money amount in its currency, whole units (Cost Management reports whole
 * subscriptions — cents are noise). Falls back to a plain number when the
 * currency is unknown or mixed (`currency: null` from the backend).
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string | null | undefined = "EUR",
): string {
  if (value == null) {
    return EM_DASH;
  }
  if (currency) {
    try {
      return value.toLocaleString(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      });
    } catch {
      // Azure reported a code Intl does not know — fall through to plain.
    }
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Compact currency for axis ticks and KPI tiles — `€1.2M`, `€45K`. */
export function formatCurrencyCompact(
  value: number | null | undefined,
  currency: string | null | undefined = "EUR",
): string {
  if (value == null) {
    return EM_DASH;
  }
  if (currency) {
    try {
      return value.toLocaleString(undefined, {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
      });
    } catch {
      // Unknown currency code — fall through to a plain compact number.
    }
  }
  return value.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

/** A 0–100 value as a percentage with one decimal, or an em dash. */
export function formatPercent(value: number | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  return `${value.toFixed(1)}%`;
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/**
 * Compact relative time — `"12 min ago"`, `"in 3 hr"`, `"just now"` under a
 * minute. For tooltips and captions; tables keep absolute timestamps.
 */
export function formatRelativeTime(value: string | number | Date | null | undefined): string {
  if (value == null) {
    return EM_DASH;
  }
  const instant = new Date(value).getTime();
  if (Number.isNaN(instant)) {
    return EM_DASH;
  }
  const delta = instant - Date.now();
  const magnitude = Math.abs(delta);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "always", style: "narrow" });
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (magnitude >= ms) {
      return formatter.format(Math.round(delta / ms), unit);
    }
  }
  return "just now";
}
